'use client';

import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 'test-001',
    name: 'Marcus Chen',
    role: 'CEO, Nexus Finance',
    company: 'Nexus Finance',
    rating: 5,
    text: 'WebMint completely transformed our digital presence. The fintech dashboard they built handles 50k concurrent users without breaking a sweat. The attention to performance and security was exactly what we needed in the financial sector.',
    project: 'Fintech Dashboard',
    result: '+94% user retention',
  },
  {
    id: 'test-002',
    name: 'Priya Sharma',
    role: 'Marketing Director, Bloom Beauty',
    company: 'Bloom Beauty',
    rating: 5,
    text: "From concept to launch in 6 weeks — WebMint delivered a Shopify store that's both beautiful and blazing fast. Our conversion rate jumped 42% in the first month. The team's e-commerce expertise is unmatched.",
    project: 'E-Commerce Platform',
    result: '+187% revenue in Q1',
  },
  {
    id: 'test-003',
    name: 'James Okafor',
    role: 'Principal, Orion Academy',
    company: 'Orion Academy',
    rating: 5,
    text: "The LMS WebMint built for us handles 8,000 students across 3 campuses. Live class integration, assignment management, parent portals — everything works flawlessly. Support team responds within 2 hours every time.",
    project: 'EdTech Platform',
    result: '+68% student engagement',
  },
  {
    id: 'test-004',
    name: 'Sofia Reyes',
    role: 'Operations Manager, Lumina Hotels',
    company: 'Lumina Hotels',
    rating: 5,
    text: 'Our hotel booking website now drives 35% of our direct reservations, up from 8%. The virtual tour feature and seamless payment flow made the biggest difference. WebMint truly understands hospitality UX.',
    project: 'Hotel Booking Platform',
    result: '+42% direct bookings',
  },
  {
    id: 'test-005',
    name: 'David Müller',
    role: 'CTO, Terra Real Estate',
    company: 'Terra Real Estate',
    rating: 5,
    text: 'The AI-powered property search and Mapbox integration they implemented reduced our support tickets by 60% — users find what they need without help. Code quality is exceptional; our engineers were impressed at the handoff.',
    project: 'Real Estate Portal',
    result: '+230% qualified leads',
  },
];

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const total = testimonials?.length;

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  const visibleIndices = [
    (current - 1 + total) % total,
    current,
    (current + 1) % total,
  ];

  return (
    <section id="testimonials" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 aurora-bg opacity-50" />
      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-14">
          <span className="section-label">Client Stories</span>
          <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
            Results That Speak for{' '}
            <span className="text-gradient-primary">Themselves</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            Don&apos;t take our word for it — hear from the businesses we&apos;ve helped grow.
          </p>
        </div>

        {/* Desktop: 3 cards */}
        <div className="hidden lg:grid grid-cols-3 gap-5 mb-8">
          {visibleIndices?.map((idx, i) => {
            const t = testimonials?.[idx];
            const isFeatured = i === 1;
            return (
              <div
                key={`test-card-${t?.id}-${i}`}
                className={`glass-card rounded-2xl p-6 border transition-all duration-500 ${
                  isFeatured
                    ? 'border-primary/40 ring-1 ring-primary/20 scale-105' :'border-border/40 opacity-70 scale-95'
                }`}
              >
                <Quote size={24} className="text-primary/30 mb-4" />
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t?.rating })?.map((_, si) => (
                    <Star key={`star-${t?.id}-${si}`} size={12} className="text-amber fill-amber" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">&ldquo;{t?.text}&rdquo;</p>
                <div className="flex items-center justify-between pt-4 border-t border-border/40">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xs font-bold text-white">
                      {t?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                    <div>
                      <p className="text-sm font-display font-semibold text-foreground">{t?.name}</p>
                      <p className="text-xs text-muted-foreground">{t?.role}</p>
                    </div>
                  </div>
                  <span className="text-xs font-mono-code text-emerald bg-emerald/10 px-2 py-1 rounded-full border border-emerald/20">
                    {t?.result}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile: single card */}
        <div className="lg:hidden mb-8">
          <div className="glass-card rounded-2xl p-6 border border-primary/40">
            <Quote size={24} className="text-primary/30 mb-4" />
            <div className="flex gap-0.5 mb-4">
              {Array.from({ length: testimonials?.[current]?.rating })?.map((_, si) => (
                <Star key={`mobile-star-${si}`} size={12} className="text-amber fill-amber" />
              ))}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">&ldquo;{testimonials?.[current]?.text}&rdquo;</p>
            <div className="flex items-center justify-between pt-4 border-t border-border/40">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xs font-bold text-white">
                  {testimonials?.[current]?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </div>
                <div>
                  <p className="text-sm font-display font-semibold text-foreground">{testimonials?.[current]?.name}</p>
                  <p className="text-xs text-muted-foreground">{testimonials?.[current]?.role}</p>
                </div>
              </div>
              <span className="text-xs font-mono-code text-emerald bg-emerald/10 px-2 py-1 rounded-full border border-emerald/20">
                {testimonials?.[current]?.result}
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={prev}
            className="p-2.5 glass-card rounded-xl border border-border/40 hover:border-primary/50 text-muted-foreground hover:text-foreground transition-all duration-200 active:scale-95"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {testimonials?.map((t, i) => (
              <button
                key={`dot-${t?.id}`}
                onClick={() => setCurrent(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === current ? 'w-6 bg-primary' : 'w-1.5 bg-border'
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={next}
            className="p-2.5 glass-card rounded-xl border border-border/40 hover:border-primary/50 text-muted-foreground hover:text-foreground transition-all duration-200 active:scale-95"
            aria-label="Next testimonial"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}